import { Component } from 'react';

/**
 * App-wide error boundary. Without one, an uncaught render exception unmounts
 * the entire tree and the user sees a completely blank page (the classic
 * "pantalla blanca" reported on https://ruido.ai/gestor when uploading an
 * image and clicking Generate). With the boundary in place, we keep the
 * shell visible and surface the actual error message so the user can recover
 * (refresh, navigate back, retry).
 */
export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Keep this for diagnostics in production browser DevTools — the user
    // can copy/paste the error to support and we can also pipe to remote logging later.
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary] Uncaught render error:', error, info?.componentStack);

    // Auto-recovery for DOM-mutation reconciliation errors caused by browser
    // extensions / in-page translators (Google Translate wraps React's text
    // nodes in <font> tags → removeChild/insertBefore throw "not a child of
    // this node"). These are transient: remounting the tree renders clean DOM.
    // Capped to avoid infinite loops on genuine render bugs.
    const msg = error?.message || String(error);
    const isDomMutation = /not a child of this node|removeChild|insertBefore|replaceChild/i.test(msg);
    if (isDomMutation) {
      const now = Date.now();
      if (!this.recoveryWindowStart || now - this.recoveryWindowStart > 10000) {
        this.recoveryWindowStart = now;
        this.recoveryCount = 0;
      }
      if (this.recoveryCount < 3) {
        this.recoveryCount += 1;
        // eslint-disable-next-line no-console
        console.warn('[ErrorBoundary] Auto-recovering from external DOM mutation error.');
        setTimeout(() => this.setState({ error: null }), 50);
      }
    }
  }

  handleReset = () => {
    this.setState({ error: null });
  };

  handleReload = () => {
    if (typeof window !== 'undefined') window.location.reload();
  };

  render() {
    if (this.state.error) {
      const msg = this.state.error?.message || String(this.state.error);
      return (
        <div
          data-testid="app-error-boundary"
          className="min-h-screen flex items-center justify-center p-6 bg-[#f0efed]"
        >
          <div className="max-w-md w-full bg-white/70 backdrop-blur-md border border-gray-200/60 rounded-2xl p-6 shadow-sm">
            <h2 className="text-lg font-medium text-gray-900 font-['Outfit'] mb-2">
              Something went wrong
            </h2>
            <p className="text-sm text-gray-600 mb-3 leading-relaxed">
              We hit an unexpected error rendering this view. The team has been notified.
              Try reloading the page or going back to the home screen.
            </p>
            <pre className="text-[11px] text-rose-600 bg-rose-50 border border-rose-100 rounded-lg p-3 mb-4 max-h-32 overflow-auto whitespace-pre-wrap">
              {msg}
            </pre>
            <div className="flex gap-2">
              <button
                data-testid="error-boundary-reload"
                onClick={this.handleReload}
                className="flex-1 py-2 rounded-xl text-sm font-medium bg-gray-900 hover:bg-gray-800 text-white transition-colors"
              >
                Reload
              </button>
              <a
                data-testid="error-boundary-home"
                href="/"
                className="flex-1 py-2 rounded-xl text-sm text-center text-gray-700 bg-white/60 hover:bg-white border border-gray-200/60 transition-colors"
              >
                Home
              </a>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
